import { useMemo } from "react";
import { Card, Typography } from "@/components/ui/atoms";
import type { TocItem } from "../../utils";
import { useContentsNavSync } from "./useContentsNavSync";

interface ContentsNavProps {
  items: TocItem[];
}

const linkBaseClass =
  "block rounded-md px-2 py-1.5 transition-[color,background-color,font-weight] duration-300 ease-out";
const linkActiveClass = "text-purple font-semibold";
const linkIdleClass = "text-dark-invirtus/75 hover:text-dark-invirtus";

/**
 * Summary / in-page TOC for blog articles. Presentation only; scroll sync lives in `useContentsNavSync`.
 */
export function ContentsNav({ items }: ContentsNavProps) {
  const headingIds = useMemo(() => items.map((item) => item.id), [items]);
  const { activeId, navigateToSection } = useContentsNavSync(headingIds);

  if (!items.length) return null;

  return (
    <nav aria-label="Table of contents" className="hidden w-full xl:block">
      <Card variant="elevated">
        <Typography as="p" variant="body-md" weight="bold" color="secondary">
          Summary
        </Typography>
        <Typography as="ul" className="mt-2 space-y-1">
          {items.map((item) => (
            <Typography as="li" key={item.id}>
              <Typography
                as="a"
                variant="body-sm"
                href={`#${item.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigateToSection(item.id);
                }}
                className={`${linkBaseClass} ${activeId === item.id ? linkActiveClass : linkIdleClass}`}
                aria-current={activeId === item.id ? "location" : undefined}
              >
                {item.text}
              </Typography>
            </Typography>
          ))}
        </Typography>
      </Card>
    </nav>
  );
}
